import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout';
import { Alert, Button, Card, EmptyState, OrderPill, Skeleton } from '@/components/ui';
import { useT } from '@/i18n';
import { api } from '@/api/client';
import type { Transaction } from '@/api/types';
import { formatDate, formatMoney } from '@/lib/format';

const isCheckable = (status: Transaction['status']) => status === 'pending' || status === 'created';

export const Transactions = () => {
  const t = useT();
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.transactions().then(({ transactions: list }) => {
      if (!cancelled) setTransactions(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const openInvoice = async (invoiceId: string) => {
    setBusyId(invoiceId);
    try {
      const { downloadUrl } = await api.invoice(invoiceId);
      if (downloadUrl) window.location.href = downloadUrl;
    } finally {
      setBusyId(null);
    }
  };

  const checkStatus = async (orderId: string) => {
    setBusyId(orderId);
    try {
      const { order } = await api.orders.status(orderId);
      setTransactions((current) => (current ?? []).map((tx) => (tx.orderId === orderId ? { ...tx, ...order } : tx)));
    } finally {
      setBusyId(null);
    }
  };

  const hasPending = (transactions ?? []).some((tx) => isCheckable(tx.status));

  return (
    <AppShell>
      <h1 className="mb-5 text-h1 text-ink">{t('tx.title')}</h1>

      {hasPending && (
        <div className="mb-4">
          <Alert tone="warn">{t('tx.pendingBanner')}</Alert>
        </div>
      )}

      {transactions === null ? (
        <Skeleton lines={4} />
      ) : transactions.length === 0 ? (
        <EmptyState title={t('tx.empty')} body="" />
      ) : (
        <ul className="space-y-3">
          {transactions.map((tx) => (
            <li key={tx.orderId}>
              <Card className="flex flex-col gap-3 nav:flex-row nav:items-center nav:justify-between">
                <div className="flex flex-col gap-3 nav:flex-1 nav:flex-row nav:items-center nav:gap-6">
                  <div className="nav:w-32">
                    <p className="text-caption text-ink-muted">{t('tx.date')}</p>
                    <p className="text-label text-ink">{formatDate(tx.createdAt)}</p>
                  </div>
                  <div className="nav:flex-1">
                    <p className="text-caption text-ink-muted">{t('tx.report')}</p>
                    <p className="text-label text-ink">{tx.reportRef ?? tx.reportId.slice(0, 8)}</p>
                    {tx.failureReason && <p className="mt-1 text-caption text-ink-muted">{tx.failureReason}</p>}
                  </div>
                  <div className="nav:w-28">
                    <p className="text-caption text-ink-muted">{t('tx.amount')}</p>
                    <p className="text-label text-ink">{formatMoney(tx.amount, tx.currency)}</p>
                  </div>
                  <div>
                    <p className="text-caption text-ink-muted">{t('tx.status')}</p>
                    <OrderPill status={tx.status} className="mt-1" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {tx.invoiceId && (
                    <Button
                      variant="secondary"
                      loading={busyId === tx.invoiceId}
                      onClick={() => void openInvoice(tx.invoiceId!)}
                    >
                      {t('tx.invoice')}
                    </Button>
                  )}
                  {isCheckable(tx.status) && (
                    <Button
                      variant="secondary"
                      loading={busyId === tx.orderId}
                      onClick={() => void checkStatus(tx.orderId)}
                    >
                      {t('checkout.check')}
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
};
